/*
This is a generic JSONAPI resource store providing simple CRUD access.
To use it in your components, do this:

const ThingStore = Resource('things')

- Replace `ThingStore` something descriptive (I recommend <RailsModel>Store)
- Replace `things` with the JSONAPI resource name (usually pluralized)

Example: to create an `Agency` store:

const AgencyStore = Resource('agencies')

This will use the `/agencies` endpoint on the API include the correct `type`
on all post/put/patch requests.
*/

import {camelCase, isEmpty} from 'lodash'
import api from './jsonapi'


const Resource = (resource) => {
  const endpoint = `/${resource}`

  // Returns included data referenced by main data object
  // Must be attached to each item on result.data (see findAll)
  // If you're wondering, yes, it's a closure!
  //
  // The end result is being able to do things like:
  //   product.included('client') => client (for 1:1, belongs_to)
  //   client.included('products') => array of products (for 1:many)
  //   client.included('products', 1) => product (for 1:many)
  const _includedClosure = (itemData, included, type, id) => {
    // Don't waste time if there's nothing to do
    if (!itemData.relationships || !itemData.relationships[type].data) return {}
    // Get included references
    let includedRefs = itemData.relationships[type].data
    if (includedRefs instanceof Array && !id) { // 1:many, return all
      return includedRefs.map(
        (ref) => included.filter(
          (item) => (item.type === ref.type && item.id === ref.id)
        )[0]
      )
    }
    else if (includedRefs instanceof Array && id) { // 1:many, return 1
      return includedRefs.map(
        (ref) => included.filter(
          (item) => (item.type === ref.type && Number(item.id) === Number(id))
        )[0]
      )[0] || {}
    }
    else { // 1:1, return 1
      return included.filter(
        (item) => (
          item.type === includedRefs.type && item.id === includedRefs.id
        )
      )[0] || {}
    }
  }


  /*
  * PUBLIC METHODS
  */

  // Creates a properly formatted resource entity
  const makeEntity = (attributes, id) => ({
    id: id,
    type: camelCase(resource),
    attributes: attributes
  })

  // Creates properly formated JSON body for POST/PATCH
  const makeBody = (attributes, id) => ({data: makeEntity(attributes, id)})

  // Find all records matching given filters
  const findAll = (filters = {}, includes = [], pageNumber = 1, pageSize) => (
    new Promise((resolve, reject) => {
      api.getMany(endpoint, filters, includes, pageNumber, pageSize)
      .then((results) => {
        let data = results.data
        // Attach included reference fetcher
        if (results.data.included) {
          for (let item of data.data) {
            item.included = (type, id) => {
              return _includedClosure(item, results.data.included, type, id)
            }
          }
        }
        resolve(data)
      })
      .catch((error) => reject(error))
    })
  )

  // Find exactly one record matching given filters
  const findBy = (filters, includes) => new Promise((resolve, reject) => {
    findAll(filters, includes, 1, 1)
    .then((result) => resolve({data: result.data[0]}))
    .catch((error) => reject(error))
  })

  // Find record by id
  // Note this does NOT have includes!
  const find = (id) => new Promise((resolve, reject) => {
    api.get(`${endpoint}/${id}`)
    .then((result) => resolve(result.data))
    .catch((error) => reject(error))
  })

  // Create new record based on object
  const create = (data) => api.post(endpoint, makeBody(data))

  // Update record with given id with object data
  const update = (id, data) => api.patch(`${endpoint}/${id}`, makeBody(data, id))

  // Delete record with given id
  const destroy = (id) => api.delete(`${endpoint}/${id}`)

  const getApiMethod = (method) =>
  {
    let action = api.get
    switch(method) {
      case 'POST':
        action = api.post
        break
      case 'PUT':
        action = api.put
        break
      case 'PATCH':
        action = api.patch
        break
      case 'DELETE':
        action = api.delete
        break
      default:
        action = api.get
    }
    return action
  }

  // Access a non-RESTful endpoint
  const doAction = (customAction, id, method = 'GET', data = {}) => {
    method = method.toUpperCase()
    const action = getApiMethod(method)
    const customEndpoint = (
      id ?
      `${endpoint}/${id}/${customAction}` :
      `${endpoint}/${customAction}`
    )
    if (customAction.match(/GET|DELETE/) || isEmpty(data)) {
      return action(customEndpoint)
    }
    else {
      return action(customEndpoint, makeBody(data, id))
    }
  }

  const doCustomAction = (customAction, method, data) =>
  {
    method = method.toUpperCase()
    const action = getApiMethod(method)
    const customEndpoint = `${endpoint}/${customAction}`
    return action(customEndpoint, data)
  }

  // Clones an object and returns the result (only supported by some resources)
  const clone = (id) => new Promise((resolve, reject) => {
    doAction('clone', id, 'POST')
    .then((result) => resolve(result.data))
    .catch((error) => reject(error))
  })

  return {
    makeEntity,
    makeBody,
    findAll,
    findBy,
    find,
    create,
    update,
    destroy,
    doAction,
    doCustomAction,
    clone
  }
}

export default Resource



// WEBPACK FOOTER //
// ./src/stores/resource.js